import React from 'react';
import {
  useState,
  useCallback,
  useEffect
} from 'react';
import {
  Layout,
  Page,
  Card,
  Button,
  Toast,
  Stack,
  Select,
  Icon,
  Tooltip,
} from '@shopify/polaris';
import {
  MaximizeMinor,
  MinimizeMinor,
  RefreshMajor,
} from '@shopify/polaris-icons';
import { Helmet } from 'react-helmet';
import ProductCardView from './views/ProductCardView';
import ProductListView from './views/ProductListView';
import AddTagsDialog from './AddTagsDialog';
import RemoveTagsDialog from './RemoveTagsDialog';
import TagsPane from './TagsPane';
import FilterPane from './FilterPane';
import { apiFetch as fetch } from '../../common/Helpers';
import { AppContext } from '../../common/AppContext';


const resourceViews = {
  "Product": ["Card", "List"],
  // "Product": ["Card", "List"],
  // "Customer": ["List"],
  // "Order": ["List"]
};

// const resourceNames = {
//   "Product": {
//     singular: 'product',
//     plural: 'products',
//   },
//   "Customer": {
//     singular: 'customer',
//     plural: 'customers',
//   },
//   "Order": {
//     singular: 'order',
//     plural: 'orders',
//   }
// };

const resourceTypes = [
  "Product",
]

function generateQueryModel(filter) {

  var filterQueryString = null;
  var filterTaggedWith = [];

  if (filter.taggedWith != null) {
    filterTaggedWith = filter.taggedWith;
  }

  if (filter.queryValue != null) {
    filterQueryString = filter.queryValue;
  }

  return {
    "resourceType": "Product",
    filterQueryString: filterQueryString,
    filterTaggedWith: filterTaggedWith,
    filterSkipArchived: filter.skipArchived == null ? false : filter.skipArchived,
    filterNoTags: filter.noTags == null ? false : filter.noTags
  }

}

function TagExplorerPage(props) {

  const [selectedItems, setSelectedItems] = useState([]);
  const [pagesLastResourceId, setPagesLastResourceId] = useState([null]);
  const [resourcesData, setResourcesData] = useState({
    resources: [],
    totalTotalProductCount: 0,
    matchingProductCount: 0,
    lastResourceId: null
  });

  const [allSelected, setAllSelected] = useState(false);
  const [showTagsPane, setShowTagsPane] = useState(true);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionDialogExecuting, setActionDialogExecuting] = useState(false);
  const [resourceType, setResourceType] = useState("Product");
  const [resourceView, setResourceView] = useState("Card");
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const appContext = React.useContext(AppContext);
  const [toastMessage, setToastMessage] = useState(null);
  const [filter, setFilter] = useState({
    taggedWith: [],
    queryValue: null,
    skipArchived: false,
    noTags: false
  });

  const availableResourceViews = resourceViews[resourceType];
  // const resourceName = resourceNames[resourceType];

  function fetchTags() {
    let body = JSON.stringify(generateQueryModel(filter));

    fetch('api/tagExplorer/queryTags', {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
      },
      body: body
    })
      .then(response => response.json())
      .then(data => {
        setTags(data);
      });
  }

  function fetchAllTags() {
    fetch('api/tagExplorer/allTags')
      .then(response => response.json())
      .then(data => {
        setAllTags(data);
      });
  }

  function fetchProducts() {
    setIsLoadingProducts(true);
    let body = JSON.stringify({
      ...generateQueryModel(filter),
      lastResourceId: pagesLastResourceId[pagesLastResourceId.length - 1]
    });

    fetch('api/tagExplorer/queryProducts', {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
      },
      body: body
    })
      .then(response => response.json())
      .then(data => {
        setResourcesData({
          resources: data.products,
          totalProductCount: data.totalProductCount,
          matchingProductCount: data.matchingProductCount,
          lastResourceId: data.lastResourceId,
        });
        setIsLoadingProducts(false);
      });
  }

  function fetchData() {
    fetchProducts();
    fetchTags();
    fetchAllTags();
  }

  useEffect(() => {

    setIsLoadingProducts(true);
    const timer = setTimeout(() => { fetchData(); }, 1000);

    return () => {
      clearTimeout(timer);
      setIsLoadingProducts(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, pagesLastResourceId]);

  const handleOnTagClick = function (clickedTag) {
    setFilter({
      ...filter,
      taggedWith: [...filter.taggedWith, clickedTag]
    });
  };

  const handleQueryChange = function (query) {
    setFilter(query);
    setAllSelected(false);
    setSelectedItems([]);
    setPagesLastResourceId([null]);
  }
  const handleResourceTypeChange = useCallback((value) => setResourceType(value), []);
  const handleResourceViewChange = useCallback((value) => setResourceView(value), []);
  const handleSelectionChange = (value) => {
    if (value === "All") {
      setAllSelected(true);
      setSelectedItems(resourcesData.resources.map(_ => _.id));
    }
    else {
      setAllSelected(false);
      setSelectedItems(value);
    }
  };

  const executeAddTags = function (tagsToAdd) {
    let body = JSON.stringify(
      {
        query: generateQueryModel(filter),
        tags: tagsToAdd,
        allSelected: allSelected,
        selectedResourceIds: selectedItems
      });

    fetch('api/tagExplorer/operation/addTags', {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
      },
      body: body
    })
      .then(response => {
        setActionDialog(null);
        setActionDialogExecuting(false);
        setToastMessage("Bulk Operation scheduled for execution.");
      });
  }

  const executeRemoveTags = function (tags) {
    let body = JSON.stringify(
      {
        query: generateQueryModel(filter),
        tags: tags,
        allSelected: allSelected,
        selectedResourceIds: selectedItems
      });

    fetch('api/tagExplorer/operation/removeTags', {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
      },
      body: body
    })
      .then(response => {
        setActionDialog(null);
        setActionDialogExecuting(false);
        setToastMessage("Bulk Operation scheduled for execution.");
      });
  }

  const handleOnExecute = (selectedTags) => {
    setActionDialogExecuting(true);

    if (actionDialog === "addTags") {
      executeAddTags(selectedTags);
    }
    if (actionDialog === "removeTags") {
      executeRemoveTags(selectedTags);
    }

  };
  const handleOnCancel = useCallback((value) => setActionDialog(null), []);
  const handleToggleNavigation = useCallback(() => appContext.toggleNavigation(), [appContext]);
  const handleToggleTagsPane = () => setShowTagsPane(!showTagsPane);
  const handleRequestPreviousPage = () => {
    setPagesLastResourceId(pagesLastResourceId.slice(0, pagesLastResourceId.length - 1));
    setAllSelected(false);
    setSelectedItems([]);
  };
  const handleRequestNextPage = () => {
    setPagesLastResourceId([...pagesLastResourceId, resourcesData.lastResourceId]);
    setAllSelected(false);
    setSelectedItems([]);
  };
  const handleRequestRefresh = () => {
    setPagesLastResourceId([...pagesLastResourceId]);
    setAllSelected(false);
    setSelectedItems([]);
  };

  const addTagContent = <div>
    <Stack alignment="center" spacing="tight" distribution="leading">
      <Stack.Item>
        <img width="22px" alt="add tag operation" src="https://taggeniemain.blob.core.windows.net/images/single-tag-add-small.png"></img>
      </Stack.Item>
      <Stack.Item>
        <span>Add Tags</span>
      </Stack.Item>
    </Stack>
  </div>;

  const removeTagContent = <div>
    <Stack alignment="center" spacing="tight" distribution="leading">
      <Stack.Item>
        <img width="22px" alt="remove tag operation" src="https://taggeniemain.blob.core.windows.net/images/single-tag-remove-small.png"></img>
      </Stack.Item>
      <Stack.Item>
        <span>Remove Tags</span>
      </Stack.Item>
    </Stack>
  </div>;

  const promotedBulkActions = [
    {
      content: addTagContent,
      onAction: () => setActionDialog("addTags"),
    },
    {
      content: removeTagContent,
      onAction: () => setActionDialog("removeTags"),
    }
  ];

  return (
    <div>
      <Helmet>
        <title>Tag Genie - Tag Explorer</title>
      </Helmet>
      <Page title="Tag Explorer" fullWidth
        primaryAction={
          <Button
            onClick={handleToggleNavigation}
            icon={appContext.hideNavigation ? MinimizeMinor : MaximizeMinor}>{appContext.hideNavigation ? "Exit fullscreen" : "Enter fullscreen"}</Button>
        }>
        {actionDialog === "addTags" && <AddTagsDialog
          active={true}
          executing={actionDialogExecuting}
          tags={allTags}
          onExecute={handleOnExecute}
          onCancel={handleOnCancel}></AddTagsDialog>}
        {actionDialog === "removeTags" && <RemoveTagsDialog
          active={true}
          executing={actionDialogExecuting}
          tags={tags}
          onExecute={handleOnExecute}
          onCancel={handleOnCancel}></RemoveTagsDialog>}

        {toastMessage != null ? (
          <Toast duration={5000} onDismiss={() => { setToastMessage(null); }} content={toastMessage} />
        ) : null
        }
        <Layout>
          <Layout.Section>
            <Stack alignment='trailing' distribution='leading'>
              <Stack.Item>
                <Select
                  label="Resource Type"
                  options={resourceTypes}
                  value={resourceType}
                  onChange={handleResourceTypeChange}
                />
              </Stack.Item>
              <Stack.Item>
                <Select
                  label="View"
                  options={availableResourceViews}
                  value={resourceView}
                  onChange={handleResourceViewChange}
                />
              </Stack.Item>
              <Stack.Item>
                <Tooltip dismissOnMouseOut content="Refresh data">
                  <Button onClick={handleRequestRefresh} size="medium" icon={<Icon source={RefreshMajor} />}></Button>
                </Tooltip>
              </Stack.Item>
              <Stack.Item>
                <Button onClick={handleToggleTagsPane}>{showTagsPane ? "Hide Tags Pane" : "Show Tags Pane"}</Button>
              </Stack.Item>
            </Stack>
          </Layout.Section>
          <Layout.Section fullWidth>
            <Card title="Filter Conditions" sectioned>
              <FilterPane 
                query={filter} 
                onQueryChange={handleQueryChange}
                allTags={allTags} />
            </Card>
          </Layout.Section>
          <Layout.Section oneHalf={showTagsPane} fullWidth={!showTagsPane}>
            {resourceView === "Card" &&
              <ProductCardView
                onRequestNextPage={handleRequestNextPage}
                onRequestPreviousPage={handleRequestPreviousPage}
                currentPage={pagesLastResourceId.length}
                resources={resourcesData.resources}
                totalResources={resourcesData.totalProductCount}
                totalMatchingResources={resourcesData.matchingProductCount}
                isLoading={isLoadingProducts}
                onSelectionChange={handleSelectionChange}
                promotedBulkActions={promotedBulkActions}
                allSelected={allSelected}
                selectedItems={selectedItems} />
            }
            {resourceView === "List" &&
              <ProductListView
                onRequestNextPage={handleRequestNextPage}
                onRequestPreviousPage={handleRequestPreviousPage}
                currentPage={pagesLastResourceId.length}
                resources={resourcesData.resources}
                totalResources={resourcesData.totalProductCount}
                totalMatchingResources={resourcesData.matchingProductCount}
                isLoading={isLoadingProducts}
                onSelectionChange={handleSelectionChange}
                promotedBulkActions={promotedBulkActions}
                allSelected={allSelected}
                selectedItems={selectedItems} />
            }
          </Layout.Section>
          {showTagsPane &&
            <Layout.Section oneThird>
              <TagsPane tags={tags} allTags={allTags} onTagClick={handleOnTagClick} />
            </Layout.Section>
          }
        </Layout>
      </Page>
    </div>
  );
}

export default TagExplorerPage;